import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { isAfter, isBefore, subMonths, addMonths, format } from 'date-fns';
import Decimal from 'decimal.js';
import { capitalize, compact } from 'lodash-es';
import { convertStrToRichText } from 'seagull';
import { getAutoApprovalRange, isAutoApprovalValid } from '~/business-logic/autoApprovalLogic';
import { getPriceIncreaseDateRange } from '~/business-logic/priceIncreaseLogic';
import { createErrorObject, isBoolTrue } from '~/components/ImportWizard/ImportWizard.utils';
import { netTermsOptions } from '~/constants';
import { decimalFromStr } from '~/infra/utils';
import { serviceModel, DiscountModel, serviceCostModel, serviceBillingModel, BillingDayOfMonthModel } from '~/models';
import { PriceIncreaseModel, RoundPrices } from '~/models/PriceIncreaseModel';
import { ServiceSource } from '~/models/service/model';
import { makeBillUpfrontState, makeMaxCharges, makeOnApprovalBilling, makeOneTimeManualBilling, makeOngoingBilling, makeRepeatableManualBilling } from '~/models/service-billing';
import { makeFixedCost, makeRangeCost, makeVariableCost } from '~/models/service-cost';
import { fromDateStampObject, makeDateStampObject } from '~/models/time';
import { isRowOfBundle } from '../ImportServices/servicesValidation.utils';
export const errorMessages = {
    getInvalidBooleanError: (_fieldName)=>'invalid entry. Please ensure this column value is set to either "Yes" or "No"',
    billingDayError: (serviceType)=>"Invalid billing day. For monthly ".concat(serviceType, ", the billing day must be a numeric value between <i>1</i> and <i>28</i> or <i>last</i>. For weekly and biweekly ").concat(serviceType, ", it should be a day of the week (Monday - Sunday). All other ").concat(serviceType, " must not have a billing day set. Please adjust the billing day accordingly"),
    priceIncreaseOnlyForRecurringError: (serviceType)=>"Automatic yearly price increase is only available for recurring ".concat(serviceType),
    paymentTermsError: 'Payment terms can be one of the following: ' + netTermsOptions.map((v, i)=>i < netTermsOptions.length - 1 ? v : "or ".concat(v)).join(', '),
    accountantAccessError: "Invalid entry. By filling out the column you request an Accountant access in the proposal, 'please enter <i>mandatory</i> or keep the field empty'",
    optionalServiceError: 'invalid entry. Please select <i>Included</i> or <i>Not Included</i> to indicate the status of this optional service in the proposal. Leave it blank if the service is not optional',
    sameAgreementUnmatchingDataError: 'invalid entry. Please ensure this value is set identically in all rows belonging to the same agreement',
    termsAndConditionsError: 'Invalid terms and conditions file name',
    billUpfrontLastDayError: 'Invalid entry. It is not possible to bill upfront on the last day of the month. Please change the billing day',
    singlePackageError: 'Invalid entry. Agreements cannot have a single package. Ensure all services are assigned to a package, with either two or three packages in total',
    tooManyPackagesError: 'Invalid entry. Agreements cannot have more than three packages. Please reduce the number of packages to three or fewer',
    packageUnassignedServiceError: 'Invalid entry. All services must be assigned to a package or none at all. Please note, agreements cannot have more than three packages',
    introVideoUrlError: "Invalid Entry. URL at intro video is invalid. Please enter a valid URL",
    recipientDetailsIncompleteError: 'Invalid entry. Please ensure all recipient fields are filled out',
    duplicateRecipientEmailError: 'There are multiple recipients using the same email address',
    mainEmailSameAsRecipientError: 'Main email address cannot be the same as recipient email'
};
const recipientIndices = [
    1,
    2,
    3,
    4
];
const isInvalidRecipientFields = (fields)=>fields.some((val, _, arr)=>!!val !== !!arr[0]);
export const dayOfMonthOptions = ()=>new Array(28).fill(null).map((_, idx)=>idx + 1);
export class DateParseResult {
    valid() {
        return this.result.valid;
    }
    datestamp() {
        return this.result.valid ? this.result.date : null;
    }
    date() {
        return this.result.valid && this.result.date ? fromDateStampObject(this.result.date) : null;
    }
    error() {
        return this.result.valid ? null : this.result.error;
    }
    toValidateResult() {
        return this.result.valid ? true : {
            valid: false,
            errorType: this.result.error
        };
    }
    constructor(result){
        _define_property(this, "result", void 0);
        this.result = result;
    }
}
export const getAutoApprovalValidation = (inputName)=>({
        inputName,
        required: false,
        shouldMatchSameAgreementServices: true,
        validate (value) {
            const days = +value;
            const isValidAutoApprovalPeriod = !isNaN(days) && Number.isInteger(days) && isAutoApprovalValid(days);
            return value === '' || isValidAutoApprovalPeriod;
        },
        validateError: (headerName, rowNumber)=>{
            const autoApprovalRange = getAutoApprovalRange();
            return createErrorObject(headerName, rowNumber, "Invalid notice period. Please select a number between ".concat(autoApprovalRange.minDays, " and ").concat(autoApprovalRange.maxDays));
        }
    });
export function configureValidation(validator) {
    return {
        validate: (firstName)=>{
            const result = validator(firstName);
            return typeof result === 'string' ? {
                valid: false,
                errorType: result
            } : {
                valid: result
            };
        },
        validateError: (headerName, rowNumber, errorType)=>createErrorObject(headerName, rowNumber, errorType || '')
    };
}
export function validateAgreementsRecipient(recipientIndex, rowObject) {
    if (isInvalidRecipientFields([
        rowObject["recipient".concat(recipientIndex, "Email")],
        rowObject["recipient".concat(recipientIndex, "FirstName")],
        rowObject["recipient".concat(recipientIndex, "LastName")],
        rowObject["recipient".concat(recipientIndex, "RequiredToSign")]
    ])) return createErrorObject("recipient ".concat(recipientIndex), rowObject.lineNumber, errorMessages.recipientDetailsIncompleteError);
}
export function validateAgreementsRecipients(rowObject) {
    const recipientEmails = compact([
        rowObject.recipient1Email,
        rowObject.recipient2Email,
        rowObject.recipient3Email,
        rowObject.recipient4Email
    ]);
    if (new Set(recipientEmails).size !== recipientEmails.length) {
        return createErrorObject('recipient emails', rowObject.lineNumber, errorMessages.duplicateRecipientEmailError);
    }
    if (recipientEmails.includes(rowObject.email)) {
        return createErrorObject('email', rowObject.lineNumber, errorMessages.mainEmailSameAsRecipientError);
    }
    for (const recipientIndex of recipientIndices){
        const error = validateAgreementsRecipient(recipientIndex, rowObject);
        if (error) {
            return error;
        }
    }
}
export function getDraftReviewers(rowObject) {
    return compact(recipientIndices.map((recipientIndex)=>{
        const email = rowObject["recipient".concat(recipientIndex, "Email")];
        const firstName = rowObject["recipient".concat(recipientIndex, "FirstName")];
        const lastName = rowObject["recipient".concat(recipientIndex, "LastName")];
        const requiredToSign = rowObject["recipient".concat(recipientIndex, "RequiredToSign")];
        return email && firstName && lastName && requiredToSign && {
            email,
            firstName,
            lastName,
            isSignatory: isBoolTrue(requiredToSign)
        };
    }));
}
export function validatePackages(serviceRows) {
    const [packageNames, hasServicesWithoutPackageName] = serviceRows.reduce((acc, rowObject)=>{
        const emptyPackageName = rowObject.packages === '';
        return [
            !emptyPackageName ? acc[0].add(rowObject.packages) : acc[0],
            acc[1] || emptyPackageName
        ];
    }, [
        new Set([]),
        false
    ]);
    if (packageNames.size === 1) {
        return createErrorObject('packages', serviceRows[0].lineNumber, errorMessages.singlePackageError);
    }
    if (packageNames.size > 3) {
        return createErrorObject('packages', serviceRows[0].lineNumber, errorMessages.tooManyPackagesError);
    }
    if (packageNames.size > 0 && hasServicesWithoutPackageName) {
        return createErrorObject('packages', serviceRows[0].lineNumber, errorMessages.packageUnassignedServiceError);
    }
}
export function parseDateString(dateString) {
    if (!dateString) {
        return new DateParseResult({
            valid: true,
            date: null
        });
    }
    const result1 = dateString.match(/^(?:((?:0?(?:1|3|5|7|8))|(?:10|12))[-/]((?:[012]?[0-9])|(?:3[01]))|(?:((?:0?(?:4|6|9))|(?:11))[-/]((?:[012]?[0-9])|(?:30)))|(?:(0?(?:2))[-/]([012]?[0-9])))[-/](\d{4})$/);
    if (result1) {
        const [, month1, day1, month2, day2, month3, day3, year] = result1;
        const month = month1 || month2 || month3;
        const day = day1 || day2 || day3;
        return new DateParseResult({
            valid: true,
            date: makeDateStampObject(+year, +month, +day)
        });
    }
    const result2 = dateString.match(/^(\d{4})[-/](?:((?:[012]?[0-9])|(?:3[01]))[-/]((?:0?(?:1|3|5|7|8))|(?:10|12))|(?:((?:[012]?[0-9])|(?:30))[-/]((?:0?(?:4|6|9))|(?:11)))|(?:([012]?[0-9])[-/](0?(?:2))))$/);
    if (result2) {
        const [, year, day1, month1, day2, month2, day3, month3] = result2;
        const month = month1 || month2 || month3;
        const day = day1 || day2 || day3;
        return new DateParseResult({
            valid: true,
            date: makeDateStampObject(+year, +month, +day)
        });
    }
    return new DateParseResult({
        valid: false,
        error: 'Enter a valid date in the following format: mm-dd-yyyy'
    });
}
export function parseEffectiveDate(effectiveDate) {
    let now = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : new Date();
    const parsed = parseDateString(effectiveDate);
    const date = parsed.date();
    if (date && (isBefore(date, subMonths(now, 24)) || isAfter(date, addMonths(now, 12)))) {
        return new DateParseResult({
            valid: false,
            error: 'Date should be from 24 months in the past to 12 months in the future'
        });
    }
    return parsed;
}
export function parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now) {
    const parsed = parseDateString(priceIncreaseStartsOn || '');
    const date = parsed.date();
    if (date) {
        const parsedEffectiveDate = parseEffectiveDate(effectiveDate, now);
        const { minDate, maxDate } = getPriceIncreaseDateRange(now, parsedEffectiveDate.date());
        if (isBefore(date, minDate) || isAfter(date, maxDate)) {
            const dateFormat = (date)=>format(date, 'MM-dd-yyyy');
            return new DateParseResult({
                valid: false,
                error: "Date should be from ".concat(dateFormat(minDate), " to ").concat(dateFormat(maxDate))
            });
        }
    }
    return parsed;
}
function makeServiceParticipation(optionalService) {
    const normalizedOptionalService = optionalService === null || optionalService === void 0 ? void 0 : optionalService.toLowerCase().trim();
    if ([
        'included',
        'not included'
    ].includes(normalizedOptionalService)) {
        const includedByDefault = normalizedOptionalService === 'included';
        return {
            type: 'optional',
            optional: {
                includedByDefault
            }
        };
    } else {
        return {
            type: 'mandatory'
        };
    }
}
function makeServiceCost(rowObject, param) {
    let { now } = param, { useBundleFields, customRate } = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        useBundleFields: false
    };
    const { discountType, effectiveDate, priceIncreaseRate, priceIncreaseStartsOn } = rowObject;
    const rate = customRate !== null && customRate !== void 0 ? customRate : rowObject.rate;
    const pricing = useBundleFields ? rowObject.bundlePricing : rowObject.pricing;
    const unitName = useBundleFields ? rowObject.bundleUnitName : rowObject.unitName;
    const unitCap = useBundleFields ? rowObject.bundleUnitCap : rowObject.unitCap;
    const discount = decimalFromStr(rowObject.discount);
    const priceIncrease = priceIncreaseRate ? PriceIncreaseModel.make(RoundPrices.None, new Decimal(priceIncreaseRate), parsePriceIncreaseStartsOn(priceIncreaseStartsOn, effectiveDate, now).date()) : PriceIncreaseModel.makeNone();
    const discountObject = discount != null && discount ? (discountType === null || discountType === void 0 ? void 0 : discountType.toLowerCase()) === 'flat' ? DiscountModel.makeValue(discount.toNumber()) : DiscountModel.makePercent(discount.toNumber()) : null;
    switch(pricing === null || pricing === void 0 ? void 0 : pricing.toLowerCase()){
        case 'fixed':
            {
                return makeFixedCost(decimalFromStr(rate), {
                    discount: discountObject,
                    priceIncrease
                });
            }
        case 'hourly':
        case 'quantity':
            return makeVariableCost(decimalFromStr(rate), {
                unitName,
                unitCap: decimalFromStr(unitCap) || new Decimal(0),
                discount: discountObject,
                priceIncrease
            });
        case 'range':
            {
                const [first, second] = rate.split('-').map((p)=>p.trim());
                const hasMin = Boolean(second); // when there is only one number > it is the maximum price
                return makeRangeCost(decimalFromStr(second || first), {
                    minPrice: hasMin ? decimalFromStr(first) : null,
                    discount: discountObject,
                    priceIncrease
                });
            }
        default:
            throw new Error('bad service pricing configuration');
    }
}
export function getIsRecurring(rowObject) {
    var _this;
    return ((_this = isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence) === null || _this === void 0 ? void 0 : _this.toLowerCase()) !== 'one time';
}
export function getIsPaused(rowObject) {
    return isBoolTrue(rowObject.startPaused || '');
}
export function getSkipBillingOnAcceptance(rowObject) {
    return !isBoolTrue(rowObject.issueInvoiceOnAcceptance || '');
}
function makeServiceBilling(rowObject) {
    const { billedUpfront, automaticBilling, billingDay } = rowObject;
    const billingOccurrence = isRowOfBundle(rowObject) ? rowObject.bundleBillingOccurrence : rowObject.billingOccurrence;
    const isRecurring = getIsRecurring(rowObject);
    const isAutomaticBilling = isBoolTrue(automaticBilling);
    const isMonthly = 'monthly' === billingOccurrence.toLowerCase();
    const isWeekly = [
        'weekly',
        'biweekly'
    ].includes(billingOccurrence.toLowerCase());
    const isBilledUpfront = isBoolTrue(billedUpfront);
    const billingDayOfMonth = isMonthly ? makeBillingDayOfMonth(billingDay) : null;
    const billingDayOfWeek = isWeekly ? capitalize(billingDay) : null;
    const recurrencePeriod = billingOccurrence.toLowerCase();
    const isPaused = getIsPaused(rowObject);
    const skipBillingOnAcceptance = getSkipBillingOnAcceptance(rowObject);
    if (isAutomaticBilling && !isRecurring) {
        return makeOnApprovalBilling();
    }
    if (!isAutomaticBilling && !isRecurring) {
        return makeOneTimeManualBilling();
    }
    if (!isAutomaticBilling && isRecurring) {
        return makeRepeatableManualBilling(recurrencePeriod, {
            maxCharges: makeMaxCharges(false)
        });
    }
    return makeOngoingBilling(recurrencePeriod, {
        isAwaitingApproval: true,
        billingDayOfMonth,
        billingDayOfWeek,
        maxCharges: makeMaxCharges(false),
        isProrated: false,
        isPaused,
        isBilledUpfront,
        billUpfrontState: makeBillUpfrontState(skipBillingOnAcceptance)
    });
}
function makeBillingDayOfMonth(billingDay) {
    return BillingDayOfMonthModel.make(billingDay.toLowerCase() === 'last' ? 'last' : parseInt(billingDay));
}
export const createServiceFromCsvRow = (rowObject, context)=>{
    const { serviceName, serviceDescription, optionalService } = rowObject;
    const { generateUniqueId } = context;
    return {
        id: generateUniqueId('service'),
        name: serviceName,
        description: convertStrToRichText(serviceDescription),
        billing: serviceBillingModel.toDTO(makeServiceBilling(rowObject)),
        cost: serviceCostModel.toDTO({
            cost: makeServiceCost(rowObject, context),
            shouldResetDiscount: false
        }),
        participation: makeServiceParticipation(optionalService),
        isBillable: false,
        serviceTemplateId: generateUniqueId('service-template'),
        status: serviceModel.statusToDTO(serviceModel.makeAwaitingApprovalStatus(), makeServiceCost(rowObject, context)),
        source: ServiceSource.Draft
    };
};
function createIncludedServiceFromCsvRow(rowObject, context) {
    var _price_price;
    const { generateUniqueId } = context;
    const price = serviceCostModel.toDTO({
        cost: makeServiceCost(rowObject, context),
        shouldResetDiscount: true
    });
    return {
        id: generateUniqueId('included-service'),
        name: rowObject.serviceName,
        description: convertStrToRichText(rowObject.serviceDescription),
        rate: ((_price_price = price.price) !== null && _price_price !== void 0 ? _price_price : price.maxPrice) * 100,
        serviceTemplateId: null
    };
}
export function createServiceBundleFromCsvRow(rowObjects, context) {
    const firstRow = rowObjects[0];
    const { bundleName, bundleDescription, optionalService } = firstRow;
    const { generateUniqueId } = context;
    let accumulatedCost = 0;
    const includedServicesToCreateAsServices = [];
    const includedServices = [];
    rowObjects.forEach((row)=>{
        const includedService = createIncludedServiceFromCsvRow(row, context);
        accumulatedCost += includedService.rate;
        includedServicesToCreateAsServices.push(createServiceFromCsvRow(_object_spread_props(_object_spread({}, row), {
            discount: '',
            discountType: '',
            automaticBilling: 'no'
        }), context));
        includedServices.push(includedService);
    });
    const bundleService = {
        id: generateUniqueId('service'),
        name: bundleName,
        description: convertStrToRichText(bundleDescription || ''),
        billing: serviceBillingModel.toDTO(makeServiceBilling(firstRow)),
        cost: serviceCostModel.toDTO({
            cost: makeServiceCost(firstRow, context, {
                useBundleFields: true,
                customRate: accumulatedCost.toString()
            }),
            shouldResetDiscount: false
        }),
        participation: makeServiceParticipation(optionalService),
        isBillable: false,
        serviceTemplateId: generateUniqueId('service-template'),
        status: serviceModel.statusToDTO(serviceModel.makeAwaitingApprovalStatus(), makeServiceCost(firstRow, context)),
        source: ServiceSource.Draft,
        bundle: {
            services: includedServices
        }
    };
    return {
        bundleService,
        includedServicesToCreateAsServices
    };
}
